// #62, the booking bar (owner trial, 2026-10-10). Where, day, time and
// guests in one pewter bar, then List / Map and Filters. Everything it sets
// lives in the URL (see lib/hub-when.ts), so Back, refresh, a shared link and
// the language switch keep it, and the cards below carry it into the venue's
// booking sheet.
//
// TO REMOVE THE TRIAL: set BOOKING_BAR_ENABLED to false. The page then draws
// its old controls row; nothing else reads the flag.
import { useMemo, type ReactNode } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Icon } from "@majourneys/icons";
import { SelectMenu, type MenuOption } from "@majourneys/ui";
import { RTL_LANGS, getMoroccoNow, type Lang } from "@majourneys/shared";
import { BEVEL_CHIP_ON, BEVEL_INSET_WHITE, BEVEL_NAVY_FACE, BEVEL_WHITE_FACE, CONTROL_EDGE, CONTROL_LIT, solid } from "@majourneys/shared/ui";
import { panelStyle } from "../../../shared/ui/card-frame";
import { menuLabels } from "../../../shared/ui/menu-labels";
import { useClientNow } from "../hooks/use-client-now";
import { MAX_BAR_GUESTS, OPEN_HOURS_HUBS, hubTimeOptions, patchSearch, readHubWhen } from "../lib/hub-when";
import { openHubFilters, useHubFiltersCount } from "./hub-dialogs";

/** The trial switch. `false` removes the bar and restores the old controls. */
export const BOOKING_BAR_ENABLED = true;

/** Whether this category page shows the bar. */
export function showBookingBar(category: string | undefined): boolean {
  return BOOKING_BAR_ENABLED && !!category && OPEN_HOURS_HUBS.has(category);
}

const NAVY = "#110E39";
const MUTED = "#5a5d77";
const GOLD_DARK = "#8A6425";
const CONTROL = { background: CONTROL_LIT, boxShadow: `0 0 0 1px ${CONTROL_EDGE}` } as const;
const ANY = "any";
/** How many days ahead the day picker lists (today included). */
const DAYS_AHEAD = 14;

/** "2026-10-12" plus n days, on the calendar (no zone shift). */
function addDays(iso: string, n: number): string {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** "Mon 12 Oct" in the page's language, Latin digits in every locale. */
export function formatBarDay(iso: string, lang: string): string {
  return new Intl.DateTimeFormat(`${lang}-u-nu-latn`, {
    weekday: "short",
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  }).format(new Date(`${iso}T12:00:00Z`));
}

export function HubBookingBar({
  lang,
  category,
  search,
  where,
  map,
}: {
  lang: string;
  category: string;
  search: Record<string, unknown>;
  /** The page's city picker, as it is. */
  where: ReactNode;
  /** The List / Map switch, on the hubs that have a map. */
  map?: { on: boolean; onChange: (next: boolean) => void };
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const when = readHubWhen(search);
  const filterCount = useHubFiltersCount();
  // "Today" and "Tomorrow" depend on the clock, so they are only named after
  // mount; the server and the first paint show the plain date.
  const now = useClientNow();
  const today = now ? getMoroccoNow(now).date : null;

  const set = (patch: Record<string, unknown>) =>
    navigate({
      search: ((prev: Record<string, unknown>) => patchSearch(prev, patch)) as never,
      resetScroll: false,
    });

  const dayLabel = (iso: string) =>
    iso === today
      ? t("explore.bar.today")
      : today && iso === addDays(today, 1)
        ? t("explore.bar.tomorrow")
        : formatBarDay(iso, lang);

  const dayOptions = useMemo<MenuOption[]>(() => {
    const days = today ? Array.from({ length: DAYS_AHEAD }, (_, i) => addDays(today, i)) : [];
    if (when.date && !days.includes(when.date)) days.unshift(when.date);
    return [{ value: ANY, label: t("explore.bar.any_day") }, ...days.map((d) => ({ value: d, label: dayLabel(d) }))];
    // dayLabel reads today and lang, both listed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [today, when.date, lang, t]);

  const timeOptions = useMemo<MenuOption[]>(
    () => [
      { value: ANY, label: t("explore.bar.any_time") },
      ...hubTimeOptions(category).map((time) => ({ value: time, label: time })),
    ],
    [category, t],
  );

  const guestOptions = useMemo<MenuOption[]>(
    () => [
      { value: ANY, label: t("explore.bar.any_guests") },
      ...Array.from({ length: MAX_BAR_GUESTS }, (_, i) => ({
        value: String(i + 1),
        label: t("explore.bar.guests_count", { count: i + 1 }),
      })),
    ],
    [t],
  );

  return (
    <div
      className="mt-4"
      style={{ ...panelStyle(26, solid("#FAFAFC")), padding: 6 }}
      data-testid="hub-booking-bar"
      role="group"
      aria-label={t("explore.bar.label")}
    >
      <div className="flex flex-wrap items-stretch gap-1.5">
        <div className="w-full min-w-0 sm:w-auto">{where}</div>
        <div className="grid min-w-0 flex-1 grid-cols-3 gap-1.5 lg:flex lg:flex-none">
          <BarSelect
            name="hub-bar-day"
            icon="ri-calendar-line"
            label={t("explore.bar.day")}
            value={when.date ?? null}
            shown={when.date ? dayLabel(when.date) : t("explore.bar.any_day")}
            options={dayOptions}
            onChange={(v) => set({ date: v === ANY ? undefined : v })}
          />
          <BarSelect
            name="hub-bar-time"
            icon="ri-time-line"
            label={t("explore.bar.time")}
            value={when.time ?? null}
            shown={when.time ?? t("explore.bar.any_time")}
            options={timeOptions}
            onChange={(v) => set({ time: v === ANY ? undefined : v })}
          />
          <BarSelect
            name="hub-bar-guests"
            icon="ri-group-line"
            label={t("explore.bar.guests")}
            value={when.adults != null ? String(when.adults) : null}
            shown={when.adults != null ? String(when.adults) : t("explore.bar.any_guests")}
            options={guestOptions}
            // A number in the URL, as the stays search and the booking sheet write it.
            onChange={(v) => set({ adults: v === ANY ? undefined : Number(v) })}
          />
        </div>
        {/* List / Map and Filters: a desk's. A phone keeps its floating pills. */}
        <div className="ms-auto hidden items-center gap-1.5 lg:flex">
          {map ? <ViewSwitch lang={lang} on={map.on} onChange={map.onChange} /> : null}
          <button
            type="button"
            onClick={openHubFilters}
            aria-haspopup="dialog"
            className="mj-press inline-flex min-h-[44px] items-center gap-2 rounded-full px-4 text-[13px] font-semibold"
            style={{ background: BEVEL_WHITE_FACE, boxShadow: `${BEVEL_INSET_WHITE}, 0 0 0 1px ${CONTROL_EDGE}`, color: NAVY }}
          >
            <Icon name="ri-equalizer-line" className="text-[15px]" aria-hidden="true" />
            {t("filter.filters")}
            {filterCount > 0 ? (
              <span
                key={filterCount}
                className="mj-pop-in rounded-full px-1.5 text-[10.5px] font-bold tabular-nums"
                style={{ background: "rgba(229,178,101,0.25)", color: GOLD_DARK }}
              >
                {filterCount}
              </span>
            ) : null}
          </button>
        </div>
      </div>
    </div>
  );
}

/** One field of the bar: the house dropdown, with the bar's own label-over-value trigger. */
function BarSelect({
  name,
  icon,
  label,
  value,
  shown,
  options,
  onChange,
}: {
  name: string;
  icon: string;
  label: string;
  value: string | null;
  shown: string;
  options: readonly MenuOption[];
  onChange: (next: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <SelectMenu
      name={name}
      options={options}
      value={value ?? ANY}
      onChange={onChange}
      labels={menuLabels(t, label)}
      side="start"
      width={220}
      searchThreshold={99}
      // Stretch with the row so Day, Time and Guests are as tall as Where and
      // their text sits on the same centre line (inline-block would not).
      className="min-w-0"
      style={{ display: "flex" }}
      renderTrigger={({ ref, open, buttonProps }) => (
        <button
          {...buttonProps}
          ref={ref}
          aria-label={`${label}, ${shown}`}
          data-testid={name}
          className="mj-press flex min-h-[48px] w-full min-w-0 flex-1 flex-col justify-center self-stretch rounded-[18px] px-3.5 text-start lg:min-w-[150px]"
          style={{ ...CONTROL, ...(open ? { boxShadow: `0 0 0 1.5px rgba(17,14,57,0.5)` } : null) }}
        >
          <span
            className="flex items-center gap-1.5 text-[10.5px] font-semibold uppercase"
            style={{ color: MUTED, letterSpacing: "0.06em" }}
          >
            <Icon name={icon} className="text-[13px]" aria-hidden="true" />
            <span className="min-w-0 break-words">{label}</span>
          </span>
          <span className="break-words text-[13.5px] font-semibold" style={{ color: value ? NAVY : MUTED }}>
            {shown}
          </span>
        </button>
      )}
    />
  );
}

/**
 * List / Map with one sliding highlight (the motion kit's two-way switch):
 * a single layer moved with transform, mirrored on a right-to-left page.
 */
export function ViewSwitch({ lang, on, onChange }: { lang: string; on: boolean; onChange: (next: boolean) => void }) {
  const { t } = useTranslation();
  const rtl = RTL_LANGS.includes(lang as Lang);
  const shift = on ? (rtl ? "-100%" : "100%") : "0";
  return (
    <div className="relative grid grid-cols-2 rounded-full p-1" style={CONTROL} role="group" aria-label={t("explore.bar.view")}>
      <span
        aria-hidden="true"
        className="absolute inset-y-1 start-1 w-[calc(50%-4px)] rounded-full transition-transform duration-[240ms] motion-reduce:transition-none"
        style={{
          background: BEVEL_NAVY_FACE,
          boxShadow: BEVEL_CHIP_ON,
          transform: `translateX(${shift})`,
          transitionTimingFunction: "var(--mj-ease-out)",
        }}
      />
      {([false, true] as const).map((isMap) => (
        <button
          key={String(isMap)}
          type="button"
          aria-pressed={on === isMap}
          aria-label={isMap ? t("explore.bar.map") : t("explore.bar.list")}
          onClick={() => on !== isMap && onChange(isMap)}
          className="relative z-[1] grid h-9 w-11 place-items-center transition-colors duration-200"
          style={{ color: on === isMap ? "#fff" : NAVY }}
        >
          <Icon name={isMap ? "ri-map-2-line" : "ri-layout-grid-line"} className="text-[15px]" aria-hidden="true" />
        </button>
      ))}
    </div>
  );
}
